import React, { useEffect, useState } from "react";
import { apiFetch } from "../api/client";


function AdminSettingsPage() {
  const [admins, setAdmins] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const [newCompany, setNewCompany] = useState("");
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState("client_admin");
  const [newCompanyId, setNewCompanyId] = useState("");

  const [currentPassword, setCurrentPassword] = useState("");
  const [updatedPassword, setUpdatedPassword] = useState("");

  const adminId = localStorage.getItem("admin_id");
  const username = localStorage.getItem("username");

  const loadAdmins = async () => {
    try {
      const res = await apiFetch("/auth/list-admins");
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to load admins");
      setAdmins(data.admins || []);
    } catch (err) {
      setError(err.message);
    }
  };

  const loadCompanies = async () => {
    try {
      const res = await apiFetch("/auth/list-companies");
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to load companies");
      setCompanies(data.companies || []);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    loadAdmins();
    loadCompanies();
  }, []);

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    try {
      const res = await apiFetch("/auth/create-company", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ company_name: newCompany }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to create company");

      setMessage("Company created successfully.");
      setNewCompany("");
      loadCompanies();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    try {
      const res = await apiFetch("/auth/create-admin", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username: newUsername,
          password: newPassword,
          role: newRole,
          company_id: newCompanyId ? parseInt(newCompanyId, 10) : null,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to create admin");

      setMessage("Admin account created successfully.");
      setNewUsername("");
      setNewPassword("");
      setNewRole("client_admin");
      setNewCompanyId("");
      loadAdmins();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    try {
      const res = await apiFetch("/auth/change-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          admin_id: parseInt(adminId, 10),
          current_password: currentPassword,
          new_password: updatedPassword,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to change password");

      setMessage("Password changed successfully.");
      setCurrentPassword("");
      setUpdatedPassword("");
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteAdmin = async (id) => {
    if (parseInt(adminId, 10) === id) {
      setError("You cannot delete your own account from this page.");
      return;
    }

    const confirmed = window.confirm("Delete this admin account?");
    if (!confirmed) return;

    setError("");
    setMessage("");

    try {
      const res = await apiFetch(`/auth/delete-admin/${id}`, {
        method: "DELETE",
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to delete admin");

      setMessage("Admin deleted successfully.");
      loadAdmins();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="admin-settings-page">
      <div className="admin-settings-topbar">
        <div>
          <h1 className="admin-settings-title">Admin Settings</h1>
          <p className="admin-settings-subtitle">
            Manage companies, client accounts, and your admin credentials.
          </p>
        </div>

        <div className="admin-settings-user-badge">
          Signed in as {username || "Admin"}
        </div>
      </div>

      {message && <div className="admin-settings-message success">{message}</div>}
      {error && <div className="admin-settings-message error">{error}</div>}

      <div className="admin-settings-grid">
        <section className="admin-settings-card">
          <h2>Create Company</h2>
          <p>Add a new client organization to your platform.</p>

          <form onSubmit={handleCreateCompany} className="admin-settings-form">
            <input
              type="text"
              placeholder="Company name"
              value={newCompany}
              onChange={(e) => setNewCompany(e.target.value)}
              required
            />
            <button type="submit">Create Company</button>
          </form>
        </section>

        <section className="admin-settings-card">
          <h2>Change My Password</h2>
          <p>Update your super admin login credentials.</p>

          <form onSubmit={handleChangePassword} className="admin-settings-form">
            <input
              type="password"
              placeholder="Current password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
            />
            <input
              type="password"
              placeholder="New password"
              value={updatedPassword}
              onChange={(e) => setUpdatedPassword(e.target.value)}
              required
            />
            <button type="submit">Update Password</button>
          </form>
        </section>
      </div>

      <section className="admin-settings-card large">
        <h2>Create Admin / Client User</h2>
        <p>Create super admins, client admins, or client users.</p>

        <form onSubmit={handleCreateAdmin} className="admin-settings-form grid">
          <input
            type="text"
            placeholder="Username"
            value={newUsername}
            onChange={(e) => setNewUsername(e.target.value)}
            required
          />

          <input
            type="password"
            placeholder="Password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />

          <select value={newRole} onChange={(e) => setNewRole(e.target.value)}>
            <option value="client_admin">client_admin</option>
            <option value="client_user">client_user</option>
            <option value="super_admin">super_admin</option>
          </select>

          <select value={newCompanyId} onChange={(e) => setNewCompanyId(e.target.value)}>
            <option value="">No company</option>
            {companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.company_name}
              </option>
            ))}
          </select>

          <button type="submit">Create Account</button>
        </form>
      </section>

      <section className="admin-settings-card large">
        <div className="admin-settings-table-header">
          <div>
            <h2>Admin Accounts</h2>
            <p>Review and manage users across your platform.</p>
          </div>
        </div>

        <div className="admin-settings-table-wrap">
          <table className="admin-settings-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Username</th>
                <th>Role</th>
                <th>Company</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {admins.length > 0 ? (
                admins.map((admin) => (
                  <tr key={admin.id}>
                    <td>{admin.id}</td>
                    <td>{admin.username}</td>
                    <td>
                      <span className={`role-badge role-${admin.role}`}>
                        {admin.role}
                      </span>
                    </td>
                    <td>{admin.company_name || "—"}</td>
                    <td>
                      <button
                        className="delete-admin-button"
                        onClick={() => handleDeleteAdmin(admin.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" style={{ textAlign: "center", padding: "20px" }}>
                    No admin accounts found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

export default AdminSettingsPage;